import React, { useEffect } from 'react';
import { List } from 'immutable';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';

import {
  CoreLink,
  Paragraph, View,
} from '@uncinc/react-drupal-core';

import {
  Container,
} from '@uncinc/uncinc-react-kitchen-sink';
import {
  getVacanciesByDistance,
} from '../../../../store/actions/UI';
import format from '../../../../helpers/date';

import {
  Breadcrumbs,
  WysiwygField,
  FadeWrapper,
} from '../../../../components';
import { BREADCRUMB_URLS } from '../../../../config/constants';

import './style.scss';

const NodeVacancyDetail = ({ entity, className }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const currentLanguage = useSelector((state) => state.getIn(['ui', 'language']));

  const title = entity.getIn(['title', 0, 'value']);
  const body = entity.getIn(['body', 0, 'processed'], '');
  const summary = entity.getIn(['body', 0, 'summary'], '');
  const date = entity.getIn(['created', 0, 'value']);
  const vacancyFunctions = entity.getIn(['field_functions'], List());
  const vacancyType = entity.getIn(['field_vacancy_type', 0, 'target_id']);
  const lat = entity.getIn(['field_location', 0, 'entity', 'field_geo_location', 0, 'lat']);
  const lon = entity.getIn(['field_location', 0, 'entity', 'field_geo_location', 0, 'lon']);
  const vacancyIdsByDistance = useSelector((state) => state.getIn(['uiInternal', 'vacancy_ids_by_distance', 'ids'], List()));

  useEffect(() => {
    if (lat && lon) {
      dispatch(getVacanciesByDistance(
        lat,
        lon,
        vacancyType,
        0,
        3,
      ));
    }
  }, [dispatch]);

  return (
    <article className="NodeVacancyDetail">
      <Container large>
        <Breadcrumbs
          data={[
            { url: BREADCRUMB_URLS.vacancies[currentLanguage].about_us, title: t('breadcrumbs.about_us') },
            { url: BREADCRUMB_URLS.vacancies[currentLanguage].overview, title: t('entities.vacancies') },
            { title: title },
          ]}
        />
      </Container>

      {/* Start of Preface */}
      <Container small>
        <div className="Preface">
          <div className="Preface__content">
            {t('entities.vacancy')}, <span>{format((new Date(date)), 'd MMMM y')}</span>
          </div>
          <div className="Preface__title">
            <h1>{title}</h1>
          </div>
          <div className="Preface__body">
            <WysiwygField
              property="schema:text"
              html={summary}
            />
          </div>
          {vacancyFunctions.size > 0 && (
            <div className="Preface__subjects">
              {vacancyFunctions.map((func) => {
                const funcEntity = func.get('entity');
                const uuid = funcEntity.getIn(['uuid', 0, 'value']);
                return (
                  <span key={uuid} className="Label Label--default">
                    {funcEntity.getIn(['name', 0, 'value'])}
                  </span>
                );
              })}
            </div>
          )}
        </div>
      </Container>
      {/* End of Preface */}

      {/* Start of Body */}
      <Container small>
        <WysiwygField
          property="schema:text"
          html={body}
        />
      </Container>
      {/* End of Body */}

      {/* Start of paragraphs */}
      {entity.get('field_paragraphs') ? (
        <div className="node--page--paragraphs-wrapper">
          {entity.get('field_paragraphs', List()).map((paragraph) => {
            const id = paragraph.get('target_id');
            return (
              <FadeWrapper key={`paragraph:${id}`}>
                <Paragraph
                  id={id}
                  entity={paragraph.get('entity')}
                />
              </FadeWrapper>
            );
          })}
        </div>
      ) : null}
      {/* End of paragraphs */}

      <Container className="NodeVacancyDetail__related-vacancies">
        {/* Start Content */}
        <div className="row">
          <div className="col-12"><h2>{t('general.related_vacancies')}</h2></div>
          <div className="NodeVacancyDetail__inner">
            <View
              className="NodeVacancyDetail__view"
              page={0}
              args={[vacancyIdsByDistance.toJS().slice(0, 4).join('+')]}
              id="latest_vacancies"
              setCurrentPage={0}
              display="block_2"
              viewMode="latest_vacancies"
              totalPages={0}
            />
          </div>
          <div className="col-12">
            <CoreLink to={BREADCRUMB_URLS.vacancies[currentLanguage].overview}>
              {t('general.all_vacancies')}
            </CoreLink>
          </div>
        </div>
        {/* End Content */}
      </Container>
    </article>
  );
};

export default NodeVacancyDetail;
